import { produce } from 'immer';
import { BehaviorSubject, Subject } from 'rxjs';
import {
    CockpitCustomer,
    ConfirmDialogModeEnum,
    CustomerSettingsData,
    EditorModeEnum,
    LoginSettings,
    RoleEnum,
    Settings,
    SingleValueStyles,
    Stage,
    StageGroup,
    StageHeader,
    StageViewModeEnum,
    ToastMessage,
    Widget,
    WidgetData,
} from '../../models/Index';
import SettingsService from '../settingsService/SettingsService';

const bodyTheme = localStorage.getItem('theme') || 'light';

const cockpitCustomers$ = new BehaviorSubject<CockpitCustomer[]>([]);
const confirmDialogMode$ = new BehaviorSubject<ConfirmDialogModeEnum>(ConfirmDialogModeEnum.CLOSED);
const currentCustomerUuid$ = new BehaviorSubject<string>('');
const currentRoles$ = new BehaviorSubject<RoleEnum[]>([RoleEnum.VIEWER]);
const customerSettings$ = new BehaviorSubject<CustomerSettingsData>(SettingsService.getDefaultCustomerSettings());
const editorMode$ = new BehaviorSubject<EditorModeEnum>(EditorModeEnum.CLOSED);
const isLinkoutActive$ = new BehaviorSubject<boolean>(false);
const isTest$ = new BehaviorSubject<boolean>(false);
const savedStage$ = new BehaviorSubject<Stage>({} as Stage);
const selectedCustomerName$ = new BehaviorSubject<string>('VIER');
const selectedHeader$ = new BehaviorSubject<StageHeader>({} as StageHeader);
const selectedRoute$ = new Subject<string>();
const selectedStage$ = new BehaviorSubject<Stage>({} as Stage);
const selectedStageGroup$ = new BehaviorSubject<StageGroup>({} as StageGroup);
const selectedWidget$ = new BehaviorSubject<Widget>({} as Widget);
const selectedWidgetData$ = new BehaviorSubject<WidgetData>({} as WidgetData);
const settings$ = new BehaviorSubject<Settings>({} as Settings);
const singleValueStyles$ = new BehaviorSubject<SingleValueStyles>({} as SingleValueStyles);
const stageHeader$ = new BehaviorSubject<StageHeader[]>([]);
const stageGroups$ = new BehaviorSubject<StageGroup[]>([] as StageGroup[]);
const stagesOfSelectedStageGroup$ = new BehaviorSubject<StageHeader[]>([]);
const stageViewMode$ = new BehaviorSubject<StageViewModeEnum>(StageViewModeEnum.PUBLISHED);
const loginSettings$ = new BehaviorSubject<LoginSettings>(SettingsService.getDefaultLoginSettings());
const widgetData$ = new BehaviorSubject<WidgetData[]>([] as WidgetData[]);
const widgets$ = new BehaviorSubject<Widget[]>([] as Widget[]);
const toastMessage$ = new Subject<ToastMessage>();
const darkTheme$ = new BehaviorSubject<string>(bodyTheme);
const openNavigation$ = new BehaviorSubject<boolean>(false);
const isDefaultValue$ = new BehaviorSubject<boolean>(false);
const isEditorOpen$ = new BehaviorSubject<boolean>(false);
const changesMade$ = new BehaviorSubject<boolean>(false);
const chartType$ = new BehaviorSubject<string>('');

stageViewMode$.subscribe((viewMode) => {
    switch (viewMode) {
        case StageViewModeEnum.WORK_IN_PROGRESS: {
            /* 
            Set the selected stage to Work In Progress Mode
            */
            selectedStage$.next(
                produce(selectedStage$.getValue(), (draft) => {
                    draft.workInProgressGridConfig = draft.workInProgressGridConfig;
                    draft.workInProgressGridConfig.layouts.lg = draft.workInProgressGridConfig.layouts.lg.map((item) => ({
                        ...item,
                        static: false,
                    }));
                    draft.workInProgressWidgetData = draft.workInProgressWidgetData;
                }),
            );
            break;
        }
    }
});

const StateService = {
    chartType$,
    cockpitCustomers$,
    confirmDialogMode$,
    currentCustomerUuid$,
    currentRoles$,
    customerSettings$,
    editorMode$,
    isLinkoutActive$,
    isTest$,
    savedStage$,
    selectedCustomerName$,
    selectedHeader$,
    selectedRoute$,
    selectedStage$,
    selectedStageGroup$,
    selectedWidget$,
    selectedWidgetData$,
    settings$,
    singleValueStyles$,
    stageHeader$,
    stageGroups$,
    stagesOfSelectedStageGroup$,
    stageViewMode$,
    loginSettings$,
    widgetData$,
    widgets$,
    toastMessage$,
    darkTheme$,
    openNavigation$,
    isDefaultValue$,
    isEditorOpen$,
    changesMade$,
};

export default StateService;
